import QRCode from 'qrcode';

import { CANVAS_LINE_HEIGHT, CANVAS_LONG_WIDTH, CANVAS_SHORT_WIDTH } from 'src/features/statistics/utils/constants';
import { ReceiptFont } from 'src/features/statistics/utils/receiptFont';
import { ReceiptLayout } from 'src/features/statistics/utils/receiptLayout';

import type { ReceiptRow } from 'src/features/statistics/types/receiptRow';

const FONT_SIZE = 12;
const INK = '#111827';
const BASELINE = 25;

/**
 * Pixels drawn per CSS pixel.
 *
 * The receipt is laid out in small units - a 12px font on an 18px line - and the
 * canvas used to be sized in exactly those, so on any HiDPI screen the browser
 * stretched one canvas pixel over two device pixels and the whole receipt looked
 * pixelated. Drawing at this multiple and letting CSS scale it back down makes it
 * sharp, and gives a usable resolution to the downloaded JPEG as a bonus.
 */
const CANVAS_SCALE = 3;

/**
 * What to draw: structured rows where the endpoint returns them, flat text
 * otherwise.
 *
 * Rows are preferred because `details` has had the layout flattened out of it -
 * a double-width total reads as an ordinary line and a centred header sits flush
 * left. The text path stays for the fiscal-note receipt, which returns no rows.
 */
type ReceiptDrawing = {
    lines?: string[];
    rows?: ReceiptRow[];
    charsPerLine?: number;
};

/**
 * The NRA verification code, drawn module by module.
 *
 * QRCode.create is synchronous and hands back the bit matrix, so the square goes
 * straight onto the receipt canvas. Modules are whole pixels: a QR scaled by
 * interpolation may not scan.
 */
const drawQr = (
    ctx: CanvasRenderingContext2D,
    payload: string,
    canvasWidth: number,
    top: number,
    boxHeight: number,
) => {
    let matrix;
    try {
        matrix = QRCode.create(payload, { errorCorrectionLevel: ReceiptLayout.QR_ERROR_CORRECTION }).modules;
    } catch {
        // A payload the encoder dislikes must not take the whole receipt with it.
        return;
    }

    const modules = matrix.size;
    const moduleSize = Math.max(Math.floor(boxHeight / modules), 1);
    const size = moduleSize * modules;
    const left = (canvasWidth - size) / 2;

    // Centring in the raw line box leaves the QR touching the row above it and a
    // wide gap below: text is drawn at a BASELINE offset into its line box, while
    // the box the QR gets starts at the top. The difference works out to
    // 2*BASELINE - ascent - CANVAS_LINE_HEIGHT, so shift down by half of it.
    ctx.font = `${FONT_SIZE}px ${ReceiptFont.STACK}`;
    const ascent = ctx.measureText('M').actualBoundingBoxAscent || FONT_SIZE * 0.75;
    const baselineShift = BASELINE - (ascent + CANVAS_LINE_HEIGHT) / 2;
    const offsetTop = Math.max(top + (boxHeight - size) / 2 + baselineShift, top);

    ctx.fillStyle = 'white';
    ctx.fillRect(left, offsetTop, size, size);
    ctx.fillStyle = 'black';
    for (let y = 0; y < modules; y++) {
        for (let x = 0; x < modules; x++) {
            if (matrix.get(x, y)) {
                ctx.fillRect(left + x * moduleSize, offsetTop + y * moduleSize, moduleSize, moduleSize);
            }
        }
    }
    ctx.fillStyle = INK;
};

/** Canvas width and height for whichever form the receipt takes. */
export const receiptCanvasSize = ({ lines, rows, charsPerLine }: ReceiptDrawing) => {
    const structured = rows && rows.length > 0 ? rows : undefined;

    const columns = structured ? ReceiptLayout.columnsOf(structured, charsPerLine) : 0;
    const longest = structured ? columns : (lines?.reduce((acc, line) => Math.max(acc, line.length), 0) ?? 0);
    const lineCount = structured
        ? structured.reduce((total, row) => total + ReceiptLayout.heightScale(row), 0)
        : (lines?.length ?? 0);

    return {
        width: longest > 40 ? CANVAS_LONG_WIDTH : CANVAS_SHORT_WIDTH,
        height: CANVAS_LINE_HEIGHT * lineCount + CANVAS_LINE_HEIGHT,
        columns,
        structured,
    };
};

export const canvasDisplaySpecifics = (canvas: HTMLCanvasElement, drawing: ReceiptDrawing) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height, columns, structured } = receiptCanvasSize(drawing);
    if (!structured && !drawing.lines) return;

    canvas.width = width * CANVAS_SCALE;
    canvas.height = height * CANVAS_SCALE;
    // Every coordinate below stays in logical units; this maps them onto the
    // larger backing store. Row-level ctx.scale() calls multiply on top of it and
    // are undone by their own restore().
    ctx.setTransform(CANVAS_SCALE, 0, 0, CANVAS_SCALE, 0, 0);

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = INK;
    ctx.textAlign = 'center';

    if (!structured) {
        ctx.font = `${FONT_SIZE}px ${ReceiptFont.STACK}`;
        drawing.lines?.forEach((line, index) => {
            ctx.fillText(line, width / 2, BASELINE + index * CANVAS_LINE_HEIGHT);
        });
        return;
    }

    let line = 0;
    for (const row of structured) {
        const rowHeight = ReceiptLayout.heightScale(row);

        const payload = ReceiptLayout.qrPayload(row);
        if (payload) {
            drawQr(ctx, payload, width, line * CANVAS_LINE_HEIGHT, rowHeight * CANVAS_LINE_HEIGHT);
            line += rowHeight;
            continue;
        }

        const rowWidth = ReceiptLayout.widthScale(row);
        const weight = ReceiptLayout.has(row, ReceiptLayout.BOLD) ? 'bold ' : '';

        ctx.save();
        // Scaling the context rather than picking a bigger font is what keeps a
        // double-width row twice as wide at the SAME height.
        ctx.scale(rowWidth, rowHeight);
        ctx.font = `${weight}${FONT_SIZE}px ${ReceiptFont.STACK}`;
        // A taller row grows upward from its baseline, so the baseline sits on the
        // LAST line it occupies - otherwise it overlaps the row above.
        const baseline = BASELINE + (line + rowHeight - 1) * CANVAS_LINE_HEIGHT;
        ctx.fillText(ReceiptLayout.lineFor(row, columns), width / 2 / rowWidth, baseline / rowHeight);
        ctx.restore();

        line += rowHeight;
    }
};

export const generateCanvas = (drawing: ReceiptDrawing, isLoadedFont: boolean): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    if (!canvas.getContext('2d') || !isLoadedFont) return canvas;

    canvasDisplaySpecifics(canvas, drawing);
    return canvas;
};

export const downloadReceipt = (canvas: HTMLCanvasElement, fileName: string) => {
    if (!canvas) return;
    const url = canvas.toDataURL('image/jpeg');
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.jpg`;
    link.click();
};

export { CANVAS_SCALE };
export type { ReceiptDrawing };
